import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { usePoll } from "@redotech/react-util/poll";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoPlatformAuthRpcClientContext } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";
import {
  IncidentProduct,
  IncidentSeverity,
} from "@redotech/redo-model/incidents/incident";
import {
  ConversionPermissions,
  InventoryPermissions,
  MarketingPermissions,
  OrderManagementPermissions,
  SupportPermissions,
} from "@redotech/redo-model/permissions";
import {
  NotificationBannerItem,
  NotificationType,
  RedoNotificationBanner,
} from "@redotech/redo-web/arbiter-components/notification/redo-notification-banner";
import { memo, useContext, useMemo } from "react";
import { useLocalStorage } from "usehooks-ts";
import { ConversionSuiteStatusesContext } from "../../../redo-suite/conversion-cloud";
import { InventoryManagementSuiteStatusesContext } from "../../../redo-suite/inventory-cloud";
import { MarketingCloudStatusesContext } from "../../../redo-suite/marketing-cloud";
import { OrderManagementSuiteStatusesContext } from "../../../redo-suite/order-management-cloud";
import { SupportSuiteStatusesContext } from "../../../redo-suite/support-cloud";

export const IncidentsBanner = memo(function IncidentsBanner() {
  const platformRpc = useRequiredContext(RedoPlatformAuthRpcClientContext);

  const [dismissedIds, setDismissedIds] = useLocalStorage<string[]>(
    "incident-banners-dismissed",
    [],
  );

  const orderManagementStatuses = useContext(
    OrderManagementSuiteStatusesContext,
  );
  const marketingStatuses = useRequiredContext(MarketingCloudStatusesContext);
  const { supportEnabled } = useRequiredContext(SupportSuiteStatusesContext);
  const { checkoutOptimizationEnabled } = useRequiredContext(
    ConversionSuiteStatusesContext,
  );
  const { imsEnabled } = useRequiredContext(
    InventoryManagementSuiteStatusesContext,
  );

  const canViewSupport = useHasPermission(SupportPermissions.VIEW_SUPPORT);
  const canViewInventoryManagement = useHasPermission(
    InventoryPermissions.ACCESS_INVENTORY,
  );
  const canViewReturns = useHasPermission(
    OrderManagementPermissions.VIEW_RETURN,
    OrderManagementPermissions.MANAGE_PACKAGE_PROTECTION_CLAIM,
    OrderManagementPermissions.MANAGE_WARRANTY,
    OrderManagementPermissions.VIEW_ORDER,
  );
  const canViewMarketing = useHasPermission(
    MarketingPermissions.VIEW_MARKETING,
  );
  const canViewConversion = useHasPermission(
    ConversionPermissions.ACCESS_CONVERSION,
  );
  const canViewOMS = useHasPermission(
    OrderManagementPermissions.VIEW_ORDER,
    OrderManagementPermissions.SCAN_AND_VERIFY,
    OrderManagementPermissions.MANAGE_SHIPMENTS,
  );

  const incidentsLoad = usePoll(
    async (signal) => {
      const result = await platformRpc.getCurrentIncidents(null, { signal });
      return result;
    },
    Temporal.Duration.from({ minutes: 5 }),
    [platformRpc],
  );

  const onDismiss = useHandler((id: string) => {
    setDismissedIds((prev) => {
      const set = new Set(prev);
      set.add(id);
      const activeIncidents = (incidentsLoad.value || []).map(
        (incident) => incident._id,
      );
      return Array.from(set).filter((dismissedId) =>
        activeIncidents.includes(dismissedId),
      );
    });
  });

  const filteredIncidents: NotificationBannerItem[] = useMemo(() => {
    const filterMarketing = (item: { type: IncidentProduct }) => {
      return (
        item.type !== IncidentProduct.MARKETING ||
        (marketingStatuses.marketingCloudEnabled && canViewMarketing)
      );
    };

    const filterRecommerce = (item: { type: IncidentProduct }) => {
      return (
        item.type !== IncidentProduct.RECOMMERCE ||
        (imsEnabled && canViewInventoryManagement)
      );
    };

    const filterConversion = (item: { type: IncidentProduct }) => {
      return (
        item.type !== IncidentProduct.CONVERSION ||
        (checkoutOptimizationEnabled && canViewConversion)
      );
    };

    const filterSupport = (item: { type: IncidentProduct }) => {
      return (
        item.type !== IncidentProduct.SUPPORT ||
        (supportEnabled && canViewSupport)
      );
    };

    const filterReturns = (item: { type: IncidentProduct }) => {
      return (
        item.type !== IncidentProduct.RETURNS ||
        (orderManagementStatuses.returnsEnabled && canViewReturns)
      );
    };

    const filterOMS = (item: { type: IncidentProduct }) => {
      return (
        item.type !== IncidentProduct.OMS ||
        (orderManagementStatuses.omsEnabled && canViewOMS)
      );
    };

    const items = (incidentsLoad.value || [])
      .filter((incident) => !dismissedIds.includes(incident._id))
      .filter(filterMarketing)
      .filter(filterRecommerce)
      .filter(filterConversion)
      .filter(filterSupport)
      .filter(filterReturns)
      .filter(filterOMS);

    return items.map((item) => ({
      id: item._id,
      message: item.message,
      type: incidentSeverityToNotificationType[item.severity],
      actions: item.publicUrl
        ? [
            {
              actionType: "link",
              text: "Learn more",
              href: item.publicUrl,
              internal: false,
            },
          ]
        : [],
    }));
  }, [
    incidentsLoad.value,
    dismissedIds,
    marketingStatuses,
    canViewMarketing,
    imsEnabled,
    canViewInventoryManagement,
    checkoutOptimizationEnabled,
    canViewConversion,
    supportEnabled,
    canViewSupport,
    orderManagementStatuses,
    canViewReturns,
    canViewOMS,
  ]);

  return (
    <RedoNotificationBanner items={filteredIncidents} onDismiss={onDismiss} />
  );
});

const incidentSeverityToNotificationType: Record<
  IncidentSeverity,
  NotificationType
> = {
  [IncidentSeverity.ERROR]: "error",
  [IncidentSeverity.WARNING]: "warning",
  [IncidentSeverity.INFO]: "info",
};
